import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  BellIcon,
  EnvelopeIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  UserCircleIcon,
  Cog6ToothIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon
} from '@heroicons/react/24/outline';
import { BellIcon as BellIconSolid } from '@heroicons/react/24/solid';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const NotificationPrefs = () => {
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('email');

  useEffect(() => {
    // Simulate fetching notification preferences
    const fetchPreferences = () => {
      const mockPreferences = {
        // Email Notifications
        email: {
          // System & Security
          systemUpdates: true,
          maintenanceAlerts: true,
          securityAlerts: true,
          loginNotifications: true,
          passwordChanges: true,
          privacyUpdates: true,
          
          // Work & Tasks
          taskAssignments: true,
          taskDeadlines: true,
          taskCompletions: false,
          projectUpdates: true,
          meetingInvitations: true,
          meetingReminders: true,
          calendarChanges: true,
          
          // Communication
          directMessages: false,
          mentionsAndReplies: true,
          groupMessages: false,
          channelActivity: false,
          
          // Medical & Patient Care
          patientUpdates: true,
          appointmentChanges: true,
          emergencyAlerts: true,
          labResults: true,
          prescriptionReminders: false,
          
          // Training & Development
          trainingReminders: true,
          certificationExpiry: true,
          courseUpdates: true,
          webinarInvitations: false,
          
          // Administrative
          payrollUpdates: true,
          scheduleChanges: true,
          policyUpdates: true,
          complianceAlerts: true,
          
          // Reports & Analytics
          weeklyReports: true,
          monthlyReports: false,
          performanceReports: true,
          customReports: false
        },

        // Push Notifications
        push: {
          // Immediate Alerts
          emergencyAlerts: true,
          urgentMessages: true,
          criticalSystemAlerts: true,
          securityBreaches: true,
          
          // Work Related
          meetingStarting: true,
          taskDeadlines: true,
          appointmentReminders: true,
          scheduleChanges: true,
          directMessages: true,
          mentions: true,
          
          // Patient Care
          patientEmergencies: true,
          labResultsReady: true,
          appointmentCancellations: true,
          prescriptionAlerts: false,
          
          // System Status
          systemDowntime: true,
          maintenanceMode: false,
          updateAvailable: false,
          backupStatus: false
        },

        // SMS Notifications
        sms: {
          // Emergency Only
          criticalAlerts: true,
          emergencyPages: true,
          systemOutages: true,
          securityBreach: true,
          
          // Important Work
          urgentMeetings: true,
          scheduleChanges: false,
          appointmentReminders: false,
          patientEmergencies: true,
          
          // Verification
          loginVerification: true,
          passwordReset: true,
          accountChanges: true,
          
          // Optional
          weekendUpdates: false,
          holidaySchedules: false
        },

        // In-App Notifications
        inApp: {
          // Real-time Updates
          newMessages: true,
          taskUpdates: true,
          documentShares: true,
          calendarInvites: true,
          teamUpdates: true,
          
          // System Notifications
          systemMessages: true,
          featureUpdates: true,
          tips: false,
          tutorials: false,
          
          // Activity Feed
          colleagueActivity: false,
          departmentNews: true,
          achievements: true,
          milestones: true,
          
          // Productivity
          focusTime: false,
          breakReminders: false,
          goalProgress: true,
          habitTracking: false
        },

        // Notification Timing
        timing: {
          workHoursOnly: false,
          quietHoursEnabled: true,
          quietHoursStart: '22:00',
          quietHoursEnd: '07:00',
          weekendsEnabled: false,
          holidaysEnabled: false,
          timezone: 'America/New_York',
          bufferTime: 5, // minutes between similar notifications
          batchNotifications: true,
          immediateEmergency: true
        },

        // Delivery Methods
        delivery: {
          preferredMethod: 'push',
          fallbackMethod: 'email',
          emergencyMethod: 'sms',
          maxRetries: 3,
          retryDelay: 10, // minutes
          confirmDelivery: true,
          readReceipts: true,
          autoMarkRead: false
        },

        // Frequency & Grouping
        frequency: {
          instantDelivery: ['emergencyAlerts', 'criticalAlerts', 'urgentMessages'],
          hourlyDigest: ['directMessages', 'teamUpdates', 'taskUpdates'],
          dailyDigest: ['projectUpdates', 'systemUpdates', 'trainingReminders'],
          weeklyDigest: ['reports', 'analytics', 'summaries'],
          customSchedule: {
            enabled: false,
            days: ['monday', 'wednesday', 'friday'],
            time: '09:00',
            categories: ['weeklyReports', 'performanceReports']
          }
        }
      };

      setPreferences(mockPreferences);
      setLoading(false);
    };

    setTimeout(fetchPreferences, 500);
  }, []);

  const handleToggleNotification = (category, setting) => {
    setSaving(true);
    
    setTimeout(() => {
      setPreferences(prev => ({
        ...prev,
        [category]: {
          ...prev[category],
          [setting]: !prev[category][setting]
        }
      }));
      setSaving(false);
    }, 300);
  };

  const handleSelectChange = (category, setting, value) => {
    setSaving(true);
    
    setTimeout(() => {
      setPreferences(prev => ({
        ...prev,
        [category]: {
          ...prev[category],
          [setting]: value
        }
      }));
      setSaving(false);
    }, 300);
  };

  const handleBulkToggle = (category, enabled) => {
    setSaving(true);
    
    setTimeout(() => {
      const updatedCategory = {};
      Object.keys(preferences[category]).forEach(key => {
        updatedCategory[key] = enabled;
      });
      
      setPreferences(prev => ({
        ...prev,
        [category]: updatedCategory
      }));
      setSaving(false);
    }, 500);
  };

  const testNotification = (type) => {
    alert(`Test ${type} notification sent! Check your ${type} for the test message.`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'email', name: 'Email', icon: EnvelopeIcon },
    { id: 'push', name: 'Push', icon: BellIcon },
    { id: 'sms', name: 'SMS', icon: DevicePhoneMobileIcon },
    { id: 'inApp', name: 'In-App', icon: ComputerDesktopIcon },
    { id: 'timing', name: 'Timing', icon: ClockIcon },
    { id: 'delivery', name: 'Delivery', icon: SpeakerWaveIcon }
  ];

  const getCategoryLabel = (key) => {
    const categoryMap = {
      // Email categories
      systemUpdates: 'System Updates',
      maintenanceAlerts: 'Maintenance Alerts',
      securityAlerts: 'Security Alerts',
      loginNotifications: 'Login Notifications',
      passwordChanges: 'Password Changes',
      privacyUpdates: 'Privacy Updates',
      taskAssignments: 'Task Assignments',
      taskDeadlines: 'Task Deadlines',
      taskCompletions: 'Task Completions',
      projectUpdates: 'Project Updates',
      meetingInvitations: 'Meeting Invitations',
      meetingReminders: 'Meeting Reminders',
      calendarChanges: 'Calendar Changes',
      directMessages: 'Direct Messages',
      mentionsAndReplies: 'Mentions & Replies',
      groupMessages: 'Group Messages',
      channelActivity: 'Channel Activity',
      patientUpdates: 'Patient Updates',
      appointmentChanges: 'Appointment Changes',
      emergencyAlerts: 'Emergency Alerts',
      labResults: 'Lab Results',
      prescriptionReminders: 'Prescription Reminders',
      trainingReminders: 'Training Reminders',
      certificationExpiry: 'Certification Expiry',
      courseUpdates: 'Course Updates',
      webinarInvitations: 'Webinar Invitations',
      payrollUpdates: 'Payroll Updates',
      scheduleChanges: 'Schedule Changes',
      policyUpdates: 'Policy Updates',
      complianceAlerts: 'Compliance Alerts',
      weeklyReports: 'Weekly Reports',
      monthlyReports: 'Monthly Reports',
      performanceReports: 'Performance Reports',
      customReports: 'Custom Reports',
      
      // Push categories
      urgentMessages: 'Urgent Messages',
      criticalSystemAlerts: 'Critical System Alerts',
      securityBreaches: 'Security Breaches',
      meetingStarting: 'Meeting Starting',
      appointmentReminders: 'Appointment Reminders',
      mentions: 'Mentions',
      patientEmergencies: 'Patient Emergencies',
      labResultsReady: 'Lab Results Ready',
      appointmentCancellations: 'Appointment Cancellations',
      prescriptionAlerts: 'Prescription Alerts',
      systemDowntime: 'System Downtime',
      maintenanceMode: 'Maintenance Mode',
      updateAvailable: 'Update Available',
      backupStatus: 'Backup Status',
      
      // SMS categories
      criticalAlerts: 'Critical Alerts',
      emergencyPages: 'Emergency Pages',
      systemOutages: 'System Outages',
      securityBreach: 'Security Breach',
      urgentMeetings: 'Urgent Meetings',
      loginVerification: 'Login Verification',
      passwordReset: 'Password Reset',
      accountChanges: 'Account Changes',
      weekendUpdates: 'Weekend Updates',
      holidaySchedules: 'Holiday Schedules',
      
      // In-App categories
      newMessages: 'New Messages',
      taskUpdates: 'Task Updates',
      documentShares: 'Document Shares',
      calendarInvites: 'Calendar Invites',
      teamUpdates: 'Team Updates',
      systemMessages: 'System Messages',
      featureUpdates: 'Feature Updates',
      tips: 'Tips',
      tutorials: 'Tutorials',
      colleagueActivity: 'Colleague Activity',
      departmentNews: 'Department News',
      achievements: 'Achievements',
      milestones: 'Milestones',
      focusTime: 'Focus Time',
      breakReminders: 'Break Reminders',
      goalProgress: 'Goal Progress',
      habitTracking: 'Habit Tracking'
    };
    
    return categoryMap[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
  };

  return (
    <div className="min-h-screen bg-apollo-gray">
      <Header />
      {/* Custom Header for Notification Preferences */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4 sm:py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0">
                <BellIconSolid className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 sm:mr-3" />
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Notification Preferences</h1>
                  <p className="mt-1 sm:mt-2 text-sm text-gray-700">
                    Control when and how you receive notifications
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:space-x-3">
                <Link
                  to="/profile"
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <UserCircleIcon className="h-4 w-4 mr-2" />
                  View Profile
                </Link>
                <Link
                  to="/profile/settings"
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <Cog6ToothIcon className="h-4 w-4 mr-2" />
                  Settings
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow mb-6 sm:mb-8">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
            <h3 className="text-base sm:text-lg font-medium text-gray-900">Quick Actions</h3>
            <p className="mt-1 text-sm text-gray-500">
              Test your notification settings or make bulk changes
            </p>
          </div>
          <div className="px-4 sm:px-6 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <button
                onClick={() => testNotification('email')}
                className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <EnvelopeIcon className="h-4 w-4 mr-2" />
                Test Email
              </button>
              <button
                onClick={() => testNotification('push')}
                className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <BellIcon className="h-4 w-4 mr-2" />
                Test Push
              </button>
              <button
                onClick={() => testNotification('SMS')}
                className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <DevicePhoneMobileIcon className="h-4 w-4 mr-2" />
                Test SMS
              </button>
              <button
                onClick={() => handleBulkToggle(activeTab, false)}
                className="inline-flex items-center justify-center px-4 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
              >
                <SpeakerXMarkIcon className="h-4 w-4 mr-2" />
                Disable All
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8 overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
              >
                <tab.icon className="h-4 w-4 mr-2" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {/* Email Notifications */}
          {activeTab === 'email' && (
            <div className="space-y-6">
              {/* Bulk Controls */}
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">Email Notifications</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Configure which email notifications you want to receive
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleBulkToggle('email', true)}
                        className="text-sm text-green-600 hover:text-green-800"
                      >
                        Enable All
                      </button>
                      <button
                        onClick={() => handleBulkToggle('email', false)}
                        className="text-sm text-red-600 hover:text-red-800"
                      >
                        Disable All
                      </button>
                    </div>
                  </div>
                </div>
                <div className="px-6 py-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {Object.entries(preferences.email).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">
                            {getCategoryLabel(key)}
                          </h4>
                          <p className="text-xs text-gray-500">
                            {key.includes('security') && 'Important security-related notifications'}
                            {key.includes('task') && 'Task and project management updates'}
                            {key.includes('meeting') && 'Calendar and meeting notifications'}
                            {key.includes('patient') && 'Patient care and medical updates'}
                            {key.includes('training') && 'Learning and development notifications'}
                            {key.includes('report') && 'Periodic reports and analytics'}
                            {!key.includes('security') && !key.includes('task') && !key.includes('meeting') && 
                             !key.includes('patient') && !key.includes('training') && !key.includes('report') && 
                             'System and administrative notifications'}
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={value}
                            onChange={() => handleToggleNotification('email', key)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Push Notifications */}
          {activeTab === 'push' && (
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Push Notifications</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Instant notifications on your devices
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleBulkToggle('push', true)}
                      className="text-sm text-green-600 hover:text-green-800"
                    >
                      Enable All
                    </button>
                    <button
                      onClick={() => handleBulkToggle('push', false)}
                      className="text-sm text-red-600 hover:text-red-800"
                    >
                      Disable All
                    </button>
                  </div>
                </div>
              </div>
              <div className="px-6 py-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Object.entries(preferences.push).map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">
                          {getCategoryLabel(key)}
                        </h4>
                        <p className="text-xs text-gray-500">
                          Real-time notification for immediate attention
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={value}
                          onChange={() => handleToggleNotification('push', key)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* SMS Notifications */}
          {activeTab === 'sms' && (
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">SMS Notifications</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Text message alerts for critical updates
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleBulkToggle('sms', true)}
                      className="text-sm text-green-600 hover:text-green-800"
                    >
                      Enable All
                    </button>
                    <button
                      onClick={() => handleBulkToggle('sms', false)}
                      className="text-sm text-red-600 hover:text-red-800"
                    >
                      Disable All
                    </button>
                  </div>
                </div>
              </div>
              <div className="px-6 py-6">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-2 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-medium text-yellow-800">SMS Usage Notice</h4>
                      <p className="text-sm text-yellow-700 mt-1">
                        SMS notifications may incur charges based on your mobile plan. We recommend enabling only critical alerts.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Object.entries(preferences.sms).map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">
                          {getCategoryLabel(key)}
                        </h4>
                        <p className="text-xs text-gray-500">
                          {key.includes('emergency') || key.includes('critical') || key.includes('urgent') 
                            ? 'High priority alert'
                            : 'Standard notification'}
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={value}
                          onChange={() => handleToggleNotification('sms', key)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* In-App Notifications */}
          {activeTab === 'inApp' && (
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">In-App Notifications</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Notifications shown within the application
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleBulkToggle('inApp', true)}
                      className="text-sm text-green-600 hover:text-green-800"
                    >
                      Enable All
                    </button>
                    <button
                      onClick={() => handleBulkToggle('inApp', false)}
                      className="text-sm text-red-600 hover:text-red-800"
                    >
                      Disable All
                    </button>
                  </div>
                </div>
              </div>
              <div className="px-6 py-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Object.entries(preferences.inApp).map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">
                          {getCategoryLabel(key)}
                        </h4>
                        <p className="text-xs text-gray-500">
                          Shown in your notification center
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={value}
                          onChange={() => handleToggleNotification('inApp', key)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Timing Settings */}
          {activeTab === 'timing' && (
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Timing & Scheduling</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Control when notifications are delivered
                </p>
              </div>
              <div className="px-6 py-6 space-y-6">
                {/* Quiet Hours */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Quiet Hours</h4>
                      <p className="text-xs text-gray-500">Suppress non-critical notifications during these hours</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.timing.quietHoursEnabled}
                        onChange={() => handleToggleNotification('timing', 'quietHoursEnabled')}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                  
                  {preferences.timing.quietHoursEnabled && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Start Time</label>
                        <input
                          type="time"
                          value={preferences.timing.quietHoursStart}
                          onChange={(e) => handleSelectChange('timing', 'quietHoursStart', e.target.value)}
                          className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">End Time</label>
                        <input
                          type="time"
                          value={preferences.timing.quietHoursEnd}
                          onChange={(e) => handleSelectChange('timing', 'quietHoursEnd', e.target.value)}
                          className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Other Timing Settings */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Work Hours Only</h4>
                      <p className="text-xs text-gray-500">Only send notifications during work hours</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.timing.workHoursOnly}
                        onChange={() => handleToggleNotification('timing', 'workHoursOnly')}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Weekend Notifications</h4>
                      <p className="text-xs text-gray-500">Receive notifications on weekends</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.timing.weekendsEnabled}
                        onChange={() => handleToggleNotification('timing', 'weekendsEnabled')}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Holiday Notifications</h4>
                      <p className="text-xs text-gray-500">Receive notifications on holidays</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.timing.holidaysEnabled}
                        onChange={() => handleToggleNotification('timing', 'holidaysEnabled')}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Batch Notifications</h4>
                      <p className="text-xs text-gray-500">Group similar notifications together</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.timing.batchNotifications}
                        onChange={() => handleToggleNotification('timing', 'batchNotifications')}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>

                {/* Buffer Time */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notification Buffer Time (minutes)
                  </label>
                  <select
                    value={preferences.timing.bufferTime}
                    onChange={(e) => handleSelectChange('timing', 'bufferTime', parseInt(e.target.value))}
                    className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value={0}>No delay</option>
                    <option value={1}>1 minute</option>
                    <option value={5}>5 minutes</option>
                    <option value={10}>10 minutes</option>
                    <option value={15}>15 minutes</option>
                    <option value={30}>30 minutes</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Minimum time between similar notifications
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Delivery Settings */}
          {activeTab === 'delivery' && (
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Delivery Settings</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Configure how notifications are delivered and handled
                </p>
              </div>
              <div className="px-6 py-6 space-y-6">
                {/* Preferred Methods */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Preferred Method
                    </label>
                    <select
                      value={preferences.delivery.preferredMethod}
                      onChange={(e) => handleSelectChange('delivery', 'preferredMethod', e.target.value)}
                      className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="push">Push Notifications</option>
                      <option value="email">Email</option>
                      <option value="sms">SMS</option>
                      <option value="inApp">In-App Only</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fallback Method
                    </label>
                    <select
                      value={preferences.delivery.fallbackMethod}
                      onChange={(e) => handleSelectChange('delivery', 'fallbackMethod', e.target.value)}
                      className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="email">Email</option>
                      <option value="sms">SMS</option>
                      <option value="push">Push Notifications</option>
                      <option value="none">None</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Emergency Method
                    </label>
                    <select
                      value={preferences.delivery.emergencyMethod}
                      onChange={(e) => handleSelectChange('delivery', 'emergencyMethod', e.target.value)}
                      className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="sms">SMS</option>
                      <option value="push">Push Notifications</option>
                      <option value="email">Email</option>
                      <option value="all">All Methods</option>
                    </select>
                  </div>
                </div>

                {/* Delivery Options */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Confirm Delivery</h4>
                      <p className="text-xs text-gray-500">Require confirmation that notifications were received</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.delivery.confirmDelivery}
                        onChange={() => handleToggleNotification('delivery', 'confirmDelivery')}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Read Receipts</h4>
                      <p className="text-xs text-gray-500">Show when notifications have been read</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.delivery.readReceipts}
                        onChange={() => handleToggleNotification('delivery', 'readReceipts')}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Auto Mark as Read</h4>
                      <p className="text-xs text-gray-500">Automatically mark notifications as read when opened</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.delivery.autoMarkRead}
                        onChange={() => handleToggleNotification('delivery', 'autoMarkRead')}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>

                {/* Retry Settings */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Max Retries
                    </label>
                    <select
                      value={preferences.delivery.maxRetries}
                      onChange={(e) => handleSelectChange('delivery', 'maxRetries', parseInt(e.target.value))}
                      className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value={0}>No retries</option>
                      <option value={1}>1 retry</option>
                      <option value={3}>3 retries</option>
                      <option value={5}>5 retries</option>
                      <option value={10}>10 retries</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Retry Delay (minutes)
                    </label>
                    <select
                      value={preferences.delivery.retryDelay}
                      onChange={(e) => handleSelectChange('delivery', 'retryDelay', parseInt(e.target.value))}
                      className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value={1}>1 minute</option>
                      <option value={5}>5 minutes</option>
                      <option value={10}>10 minutes</option>
                      <option value={15}>15 minutes</option>
                      <option value={30}>30 minutes</option>
                      <option value={60}>1 hour</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Help & Info */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start">
            <InformationCircleIcon className="h-6 w-6 text-blue-400 mr-3 mt-1 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-blue-800">Notification Tips</h3>
              <ul className="mt-2 text-sm text-blue-700 space-y-1">
                <li>• Emergency and critical alerts will always be delivered regardless of quiet hours</li>
                <li>• Test your notification settings to ensure they work as expected</li>
                <li>• Consider your mobile plan when enabling SMS notifications</li>
                <li>• In-app notifications are the most reliable and don't require external services</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Saving Indicator */}
      {saving && (
        <div className="fixed bottom-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
          Saving...
        </div>
      )}
      
      <Footer />
    </div>
  );
};

export default NotificationPrefs;
